export const listLocationsByAppInstance = /* GraphQL */ `
  query ListLocationsByAppInstance(
    $appInstance: String
    $mobVersion: ModelIntKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelLocationFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listLocationsByAppInstance(
      appInstance: $appInstance
      mobVersion: $mobVersion
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        mobVersion
        groups
        appInstance
        mssId
        title
        description
        orderNumber
        createdAt
        updatedAt
        pointMeetings {
          items {
            id
            mobVersion
            groups
            appInstance
            mssId
            title
            orderNumber
            description
            gps
            createdAt
            updatedAt
          }
          nextToken
        }
        pointSells {
          items {
            id
            mobVersion
            groups
            appInstance
            mssId
            title
            description
            orderNumber
            addressName
            addressStreet
            addressCity
            addressPostalCode
            gps
            createdAt
            updatedAt
            pointMeeting {
              id
              mobVersion
              groups
              appInstance
              mssId
              title
              description
              gps
              createdAt
              updatedAt
            }
          }
          nextToken
        }
      }
      nextToken
    }
  }
`;

export const listPointSellsByAppInstance = /* GraphQL */ `
  query ListPointSellsByAppInstance(
    $appInstance: String
    $mobVersion: ModelIntKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelPointSellFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listPointSellsByAppInstance(
      appInstance: $appInstance
      mobVersion: $mobVersion
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        mobVersion
        groups
        appInstance
        mssId
        title
        description
        orderNumber
        addressName
        addressStreet
        addressCity
        addressPostalCode
        gps
        email
        note
        createdAt
        updatedAt
        location {
          id
          mobVersion
          groups
          appInstance
          mssId
          title
          description
          orderNumber
          createdAt
          updatedAt
        }
        pointMeeting {
          id
          mobVersion
          groups
          appInstance
          mssId
          title
          description
          orderNumber
          gps
          createdAt
          updatedAt
        }
      }
      nextToken
    }
  }
`;

export const listPointSellOpenHoursByScope = /* GraphQL */ `
  query ListPointSellOpenHoursByScope(
    $scheduleScope: String
    $scheduleSort: ModelStringKeyConditionInput
    $filter: ModelPointSellOpenHoursFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listPointSellOpenHoursByScope(
      scheduleScope: $scheduleScope
      scheduleSort: $scheduleSort
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        mobVersion
        appInstance
        mssId
        pointSellMssId
        scheduleType
        dateFrom
        dateTo
        closedAllPeriod
        name
        scheduleScope
        scheduleSort
        intervals {
          items {
            id
            mssId
            dayOfWeek
            intervalNo
            opens
            closes
          }
        }
      }
      nextToken
    }
  }
`;

export const listPointMeetingsByAppInstance = /* GraphQL */ `
  query ListPointMeetingsByAppInstance(
    $appInstance: String
    $mobVersion: ModelIntKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelPointMeetingFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listPointMeetingsByAppInstance(
      appInstance: $appInstance
      mobVersion: $mobVersion
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        mobVersion
        groups
        appInstance
        mssId
        title
        description
        orderNumber
        gps
        createdAt
        updatedAt
        location {
          id
          mobVersion
          groups
          appInstance
          mssId
          title
          description
          orderNumber
          createdAt
          updatedAt
        }
        pointSell {
          id
          mobVersion
          groups
          appInstance
          mssId
          title
          description
          orderNumber
          addressName
          addressStreet
          addressCity
          addressPostalCode
          gps
          createdAt
          updatedAt
          location {
            id
            mobVersion
            groups
            appInstance
            mssId
            title
            description
            orderNumber
            createdAt
            updatedAt
          }
          pointMeeting {
            id
            mobVersion
            groups
            appInstance
            mssId
            title
            description
            orderNumber
            gps
            createdAt
            updatedAt
          }
        }
      }
      nextToken
    }
  }
`;

// The camp offer (MOB-517 spec, section 10.1, place 1; MOB-517 ticket 05). The card of a camp run
// shows the hint of the add-on, and `OrderrItemGroupComposition.offerOf` needs the type of the
// product and the Packets that hold it with their members. The rent catalogue is the same
// document, so the 2 selections are parts that only the school document gets: the rent
// catalogue loads its Packets with `getProductPacket`, and its payload does not grow.
//
// The member product selects what the function for 1 member reads. A flat add-on reads
// `productType`, `priceListType` and `price1` of the `DAILY` high season table with its VAT
// rate. The member keeps `productId`, so a member with no product in MOB (MOB-424) still names
// its product.
const SCHOOL_PRODUCT_TYPE = "productType";

// The reference of a lesson product to its companion product (MOB-542). The card of the school
// catalogue prices the pair, so the school document selects it; the rent document does not.
const SCHOOL_COMPANION_REFERENCE = "companionProductMssId";
const SCHOOL_PACKET_MEMBERS = `
                  products {
                    items {
                      productId
                      orderNumber
                      required
                      preselected
                      product {
                        id
                        mssId
                        productType
                        priceListType
                        hidden
                        priceTables {
                          numberOfPersons
                          priceListType
                          isLowSeason
                          dphPercent
                          price1
                        }
                      }
                    }
                  }`;

const categoriesByProductTypeQuery = (operationName, productTypeField, packetMembers, companionField = "") => /* GraphQL */ `
  query ${operationName}(
    $appInstance: String
    $mobVersion: Int
    $productType: ProductType
  ) {
    listCategoriesByProductType(
      appInstance: $appInstance
      mobVersionProductType: {
        eq: { mobVersion: $mobVersion, productType: $productType }
      }
    ) {
      items {
        id
        title
        description
        orderNumber
        campCourse
        mssId
        hidden
        rentalSet
        sets {
          items {
            id
            mssId
            title
            description
            priceTables {
              numberOfPersons
              priceListType
              isLowSeason
              dphPercent
              price05
              price1
              price2
              price3
              price4
              price5
              price6
              price7
              price8
              price9
              price10
              price11
              price12
              price13
              price14
              price15
              price16
              price17
              price18
              price19
              price20
              price1_5
              price2_5
              price3_5
              price4_5
              price5_5
              price6_5
              price7_5
              price8_5
              price9_5
              price10_5
              price11_5
              price12_5
              price13_5
              price14_5
              price15_5
              price16_5
              price17_5
              price18_5
              price19_5
              price20_5
              priceEveryOther
            }
            pricingMode
            products {
              items {
                orderNumber
                product {
                  id
                  mssId
                  title
                  description
                  intro
                  orderNumber
                  rentProductType
                  hidden
                  rentAllowedLengths {
                    length
                    unit
                  }
                  rentAvailabilityMode
                  pricingMode
                  images
                  sizes
                  deposit
                }
              }
            }
          }
        }
        products {
          items {
            id
            mssId
            mobVersion
            title
            description
            intro
            campCourse
            ${productTypeField}
            orderNumber
            rentProductType
            hidden
            rentAllowedLengths {
              length
              unit
            }
            rentAvailabilityMode
            pricingMode
            images
            sizes
            deposit
            schoolFormType
            ${companionField}
            masterProduct {
              id
              mobVersion
              groups
              appInstance
              mssId
              title
              description
              intro
              campCourse
              productType
              enabledFrom
              enabledTo
              pricingMode
              numberOfPersons
              minNumOfPersons
              maxNumOfPersons
              schoolFormType
              ${companionField}
              priceTables {
                numberOfPersons
                priceListType
                isLowSeason
                dphPercent
                price05
                price1
                price2
                price3
                price4
                price5
                price6
                price7
                price8
                price9
                price10
                price11
                price12
                price13
                price14
                price15
                price16
                price17
                price18
                price19
                price20
                price1_5
                price2_5
                price3_5
                price4_5
                price5_5
                price6_5
                price7_5
                price8_5
                price9_5
                price10_5
                price11_5
                price12_5
                price13_5
                price14_5
                price15_5
                price16_5
                price17_5
                price18_5
                price19_5
                price20_5
                priceEveryOther
              }
            }
            childProducts {
              items {
                id
                mobVersion
                groups
                appInstance
                mssId
                title
                description
                intro
                campCourse
                productType
                enabledFrom
                enabledTo
                pricingMode
                numberOfPersons
                minNumOfPersons
                maxNumOfPersons
                schoolFormType
                ${companionField}
                minLength
                minDailyLength
                priceTables {
                  numberOfPersons
                  priceListType
                  isLowSeason
                  dphPercent
                  price05
                  price1
                  price2
                  price3
                  price4
                  price5
                  price6
                  price7
                  price8
                  price9
                  price10
                  price11
                  price12
                  price13
                  price14
                  price15
                  price16
                  price17
                  price18
                  price19
                  price20
                  price1_5
                  price2_5
                  price3_5
                  price4_5
                  price5_5
                  price6_5
                  price7_5
                  price8_5
                  price9_5
                  price10_5
                  price11_5
                  price12_5
                  price13_5
                  price14_5
                  price15_5
                  price16_5
                  price17_5
                  price18_5
                  price19_5
                  price20_5
                  priceEveryOther
                }
              }
            }
            skill {
              id
              mssId
              title
              description
            }
            pointMeetings {
              items {
                pointMeeting {
                  id
                  mssId
                  title
                  description
                  orderNumber
                }
              }
            }
            schoolFormType
            numberOfPersons
            minNumOfPersons
            maxNumOfPersons
            minLessonLength
            maxLessonLength
            minLength
            maxLength
            minDailyLength
            maxDailyLength
            defaultDailyLength
            allowGroupCourseFlexibleStart
            groupCourseCustomLessonSelection
            defaultLessonLength
            timePreferencesStartTimes
            timePreferencesEnabledMonday
            timePreferencesEnabledTuesday
            timePreferencesEnabledWednesday
            timePreferencesEnabledThursday
            timePreferencesEnabledFriday
            timePreferencesEnabledSaturday
            timePreferencesEnabledSunday
            locations {
              items {
                location {
                  id
                  mssId
                  groups
                  appInstance
                  title
                  description
                  orderNumber
                }
              }
            }
            priceTables {
              numberOfPersons
              priceListType
              isLowSeason
              dphPercent
              price05
              price1
              price2
              price3
              price4
              price5
              price6
              price7
              price8
              price9
              price10
              price11
              price12
              price13
              price14
              price15
              price16
              price17
              price18
              price19
              price20
              price1_5
              price2_5
              price3_5
              price4_5
              price5_5
              price6_5
              price7_5
              price8_5
              price9_5
              price10_5
              price11_5
              price12_5
              price13_5
              price14_5
              price15_5
              price16_5
              price17_5
              price18_5
              price19_5
              price20_5
              priceEveryOther
            }
            sets {
              items {
                set {
                  id
                  mssId
                  title
                  products {
                    items {
                      orderNumber
                      product {
                        id
                        mssId
                        hidden
                      }
                    }
                  }
                }
              }
            }
            productPackets {
              items {
                id
                productPacketId
                preselected
                required
                productPacket {
                  id
                  mssId
                  title
                  description
                  image${packetMembers}
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const listRentCategoriesByProductType = categoriesByProductTypeQuery(
  "ListRentCategoriesByProductType",
  "",
  ""
);

// The school document keeps the operation name of the rent document. The school catalogue sent
// `ListRentCategoriesByProductType` before the camp offer, and the E2E recorder, the specs and the
// log search find the request by that name. A new name is a change of a contract that has no
// consumer to gain from it.
export const listSchoolCategoriesByProductType = categoriesByProductTypeQuery(
  "ListRentCategoriesByProductType",
  SCHOOL_PRODUCT_TYPE,
  SCHOOL_PACKET_MEMBERS,
  SCHOOL_COMPANION_REFERENCE
);

export const getProduct = /* GraphQL */ `
  query GetProduct($id: ID!) {
    getProduct(id: $id) {
      id
      mobVersion
      mssId
      title
      description
      intro
      campCourse
      productType
      enabledFrom
      enabledTo
      category {
        id
        mobVersion
        groups
        appInstance
        mssId
        title
        description
        productType
        orderNumber
        campCourse
        hidden
      }
      orderNumber
      appInstance
      images
      sizes
      deposit
      rentProductType
      hidden
      rentAllowedLengths {
        length
        unit
      }
      rentAvailabilityMode
      pricingMode
      masterProduct {
        id
        mobVersion
        groups
        appInstance
        mssId
        title
        description
        intro
        productType
        enabledFrom
        enabledTo
        pricingMode
        numberOfPersons
        minNumOfPersons
        maxNumOfPersons
        rentAvailabilityMode
        childProducts {
          items {
            id
            mobVersion
            groups
            appInstance
            mssId
            title
            description
            intro
            productType
            enabledFrom
            enabledTo
            pricingMode
            numberOfPersons
            minNumOfPersons
            maxNumOfPersons
            rentAvailabilityMode
          }
        }
      }
      childProducts {
        items {
          id
          mobVersion
          groups
          appInstance
          mssId
          title
          description
          intro
          productType
          enabledFrom
          enabledTo
          pricingMode
          numberOfPersons
          minNumOfPersons
          maxNumOfPersons
          rentAvailabilityMode
        }
      }
      skill {
        id
        mssId
        title
        description
      }
      skillLevelClients {
        items {
          id
          skillLevelClient {
            id
            mssId
            name
            description
            orderNumber
            codeSkillLevelClient
          }
          length
          startTime
        }
      }
      pointMeetings {
        items {
          pointMeeting {
            id
            mssId
            title
            description
            orderNumber
          }
        }
      }
      schoolFormType
      companionProductMssId
      ageGroups {
        items {
          id
          productId
          ageGroupId
          ageGroup {
            id
            mssId
            title
            description
            ageFrom
            ageTo
          }
        }
      }
      numberOfPersons
      minNumOfPersons
      maxNumOfPersons
      minLessonLength
      maxLessonLength
      minLength
      maxLength
      minDailyLength
      maxDailyLength
      defaultDailyLength
      allowGroupCourseFlexibleStart
      groupCourseCustomLessonSelection
      defaultLessonLength
      timePreferencesStartTimes
      timePreferencesEnabledMonday
      timePreferencesEnabledTuesday
      timePreferencesEnabledWednesday
      timePreferencesEnabledThursday
      timePreferencesEnabledFriday
      timePreferencesEnabledSaturday
      timePreferencesEnabledSunday
      customFormSchema
      priceTables {
        numberOfPersons
        priceListType
        isLowSeason
        dphPercent
        price05
        price1
        price2
        price3
        price4
        price5
        price6
        price7
        price8
        price9
        price10
        price11
        price12
        price13
        price14
        price15
        price16
        price17
        price18
        price19
        price20
        price1_5
        price2_5
        price3_5
        price4_5
        price5_5
        price6_5
        price7_5
        price8_5
        price9_5
        price10_5
        price11_5
        price12_5
        price13_5
        price14_5
        price15_5
        price16_5
        price17_5
        price18_5
        price19_5
        price20_5
        priceEveryOther
      }
      sets {
        items {
          id
          set {
            id
            mssId
            title
            products {
              items {
                orderNumber
                product {
                  id
                  mssId
                  title
                  description
                  intro
                  orderNumber
                  images
                  rentAvailabilityMode
                  rentProductType
                  hidden
                  rentAllowedLengths {
                    length
                    unit
                  }
                  pricingMode
                }
              }
            }
            priceTables {
              priceListType
              numberOfPersons
              isLowSeason
              dphPercent
              price05
              price1
              price2
              price3
              price4
              price5
              price6
              price7
              price8
              price9
              price10
              price11
              price12
              price13
              price14
              price15
              price16
              price17
              price18
              price19
              price20
              price1_5
              price2_5
              price3_5
              price4_5
              price5_5
              price6_5
              price7_5
              price8_5
              price9_5
              price10_5
              price11_5
              price12_5
              price13_5
              price14_5
              price15_5
              price16_5
              price17_5
              price18_5
              price19_5
              price20_5
              priceEveryOther
            }
            pricingMode
          }
          product {
            id
            title
            description
            intro
            orderNumber
            sizes
            deposit
            images
            rentProductType
            hidden
            rentAllowedLengths {
              length
              unit
            }
            rentAvailabilityMode
            pricingMode
            masterProduct {
              id
              mobVersion
              groups
              appInstance
              mssId
              title
              description
              intro
              productType
              enabledFrom
              enabledTo
              pricingMode
              rentAvailabilityMode
            }
          }
        }
      }
      productPackets {
        items {
          id
          productPacket {
            id
            mobVersion
            groups
            appInstance
            mssId
            title
            description
            image
            products {
              items {
                orderNumber
                required
                preselected
                product {
                  id
                  mobVersion
                  mssId
                  title
                  description
                  intro
                  orderNumber
                  images
                  sizes
                  deposit
                  productType
                  priceListType
                  rentProductType
                  hidden
                  rentAllowedLengths {
                    length
                    unit
                  }
                  rentAvailabilityMode
                  pricingMode
                  priceTables {
                    numberOfPersons
                    priceListType
                    isLowSeason
                    dphPercent
                    price05
                    price1
                    price2
                    price3
                    price4
                    price5
                    price6
                    price7
                    price8
                    price9
                    price10
                    price11
                    price12
                    price13
                    price14
                    price15
                    price16
                    price17
                    price18
                    price19
                    price20
                    price1_5
                    price2_5
                    price3_5
                    price4_5
                    price5_5
                    price6_5
                    price7_5
                    price8_5
                    price9_5
                    price10_5
                    price11_5
                    price12_5
                    price13_5
                    price14_5
                    price15_5
                    price16_5
                    price17_5
                    price18_5
                    price19_5
                    price20_5
                    priceEveryOther
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;


export const getOrderr = /* GraphQL */ `
  query GetOrderr($id: ID!) {
    getOrderr(id: $id) {
      id
      orderrNumber
      language
      discountCode
      mobVersion
      price
      priceWithoutDph
      dph
      rentDiscountPercent
      schoolDiscountPercent
      itemsCount
      expireAt
      createdAt
      updatedAt
      bookingState
      groups
      datePickUp
      dateReturn
      dateTryOut
      paymentMethod
      appInstance
      pointSell {
        id
        mobVersion
        groups
        appInstance
        mssId
        title
        description
        orderNumber
        addressName
        addressStreet
        addressCity
        addressPostalCode
        gps
        createdAt
        updatedAt
        location {
          id
          mobVersion
          groups
          appInstance
          mssId
          title
          description
          orderNumber
        }
        pointMeeting {
          id
          mobVersion
          groups
          appInstance
          mssId
          title
          description
          orderNumber
          gps
        }
      }
      payments {
        items {
          id
          mobVersion
          groups
          appInstance
          mssId
          paymentMethod
          paymentState
          amount
          currency
          transId
          redirectUrl
          createdAt
          updatedAt
        }
        nextToken
      }
      orderrItemGroups {
        items {
          id
          groups
          appInstance
          mssId
          itemsCount
          description
          numberOfPersons
          length
          priceListType
          dateStart
          dateEnd
          datePickUp
          dateReturn
          dateTryOut
          price
          priceWithoutDph
          priceBeforeDiscount
          priceBeforeDiscountWithoutDph
          dph
          dphPercent
          companion {
            productMssId
            title
            price
            priceWithoutDph
            dph
            dphPercent
            priceBeforeDiscount
            priceBeforeDiscountWithoutDph
            discountPercent
            priceListType
          }
          pointMeeting {
            id
            mobVersion
            groups
            appInstance
            mssId
            title
            description
            orderNumber
            gps
            createdAt
            updatedAt
            location {
              id
              mssId
              pointSells {
                items {
                  id
                  mssId
                  title
                  orderNumber
                  addressName
                  addressStreet
                  addressCity
                  addressPostalCode
                  gps
                }
              }
            }
          }
          pointSell {
            id
            mssId
            title
            orderNumber
            addressName
            addressStreet
            addressCity
            addressPostalCode
            gps
          }
          orderr {
            id
            price
          }
          subjectProfiles {
            name
            surname
            shoeSize
            rentAbilityLevel
            gender
            age
            weight
            height
            note
            schoolSkillLevelClientId
            language
            customFormData
            birthDate
          }
          product {
            id
            mobVersion
            groups
            appInstance
            mssId
            title
            description
            intro
            orderNumber
            productType
            pricingMode
            enabledFrom
            enabledTo
            images
            sizes
            deposit
            orderNumber
            rentProductType
            hidden
            rentAllowedLengths {
              length
              unit
            }
            rentAvailabilityMode
            schoolFormType
            numberOfPersons
            minNumOfPersons
            maxNumOfPersons
            minLessonLength
            maxLessonLength
            defaultLessonLength
            minLength
            maxLength
            minDailyLength
            maxDailyLength
            defaultDailyLength
            allowGroupCourseFlexibleStart
            groupCourseCustomLessonSelection
            timePreferencesStartTimes
            timePreferencesEnabledMonday
            timePreferencesEnabledTuesday
            timePreferencesEnabledWednesday
            timePreferencesEnabledThursday
            timePreferencesEnabledFriday
            timePreferencesEnabledSaturday
            timePreferencesEnabledSunday
            createdAt
            updatedAt
          }
          set {
            id
            title
            description
          }
          productPacket {
            id
            mssId
            title
            description
          }
          orderrItems {
            items {
              id
              language
              languages
              skillLevelClient {
                id
                mssId
                name
                codeSkillLevelClient
                orderNumber
              }
              sameInstructorRequired
              numberOfHoursRequired
              price
              priceWithoutDph
              dph
              dphPercent
              priceBeforeDiscount
              priceBeforeDiscountWithoutDph
              discountPercent
              priceListType
              product {
                id
                mssId
                title
                description
                intro
                orderNumber
                deposit
                pricingMode
                skill {
                  id
                  mssId
                  title
                  description
                }
              }
              article {
                mssId
                title
                description
                size
                sizeUnit
              }
              lessons {
                timeFrom
                timeTo
                mssWorkDayId
              }
              groupCourseDateStart
              groupCourse {
                id
                mobVersion
                appInstance
                mssId
                title
                description
                pointMeetingMssId
                dateFrom
                dateTo
                availableCapacity
                hoursPerDay
                minLength
                skillLevelClientId
                allowGroupCourseFlexibleStart
              }
              groupCourseLessons {
                id
                mobVersion
                appInstance
                mssId
                timeFrom
                timeTo
              }
            }
          }
        }
      }
    }
  }
`;

export const getPayment = /* GraphQL */ `
  query GetPayment($id: ID!) {
    getPayment(id: $id) {
      id
      mobVersion
      groups
      appInstance
      mssId
      paymentMethod
      paymentState
      amount
      currency
      transId
      redirectUrl
      createdAt
      updatedAt
      orderr {
        id
      }
    }
  }
`;

// uspornejsi varianta bez nacitani atributu content, ktery muze byt potencionalne celkem objemny
export const listStaticContentByAppInstance = /* GraphQL */ `
  query ListStaticContentByAppInstance(
    $appInstance: String
    $mobVersion: ModelIntKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelStaticContentFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listStaticContentByAppInstance(
      appInstance: $appInstance
      mobVersion: $mobVersion
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        mobVersion
        groups
        appInstance
        title
        icon
        isExternal
        linkUrl
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const createOrderrDraft = /* GraphQL */ `
  mutation CreateOrderrDraft($input: CreateOrderrDraftIn!) {
    createOrderrDraft(input: $input) {
      id
    }
  }
`;

export const addToCart = /* GraphQL */ `
  mutation AddToCart($input: AddToCartIn!) {
    addToCart(input: $input) {
      id
    }
  }
`;

export const removeFromCart = /* GraphQL */ `
  mutation RemoveFromCart($orderrItemGroupId: ID!) {
    removeFromCart(orderrItemGroupId: $orderrItemGroupId) {
      id
    }
  }
`;

export const updateOrderrPickupAndPaymentInfo = /* GraphQL */ `
  mutation UpdateOrderrPickupAndPaymentInfo(
    $orderrId: ID!
    $datePickUp: AWSDateTime!
    $paymentMethod: PaymentMethod!
    $dateTryOut: AWSDateTime
    $description: String
  ) {
    updateOrderrPickupAndPaymentInfo(
      orderrId: $orderrId
      datePickUp: $datePickUp
      paymentMethod: $paymentMethod
      dateTryOut: $dateTryOut
      description: $description
    ) {
      id
    }
  }
`;

export const checkoutOrderr = /* GraphQL */ `
  mutation CheckoutOrderr($orderrId: ID!, $subjectIn: SubjectIn!) {
    checkoutOrderr(orderrId: $orderrId, subjectIn: $subjectIn) {
      id
    }
  }
`;

export const applyDiscountCode = /* GraphQL */ `
  mutation ApplyDiscountCode($orderrId: ID!, $code: String!) {
    applyDiscountCode(orderrId: $orderrId, code: $code) {
      id
    }
  }
`;
export const removeDiscountCode = /* GraphQL */ `
  mutation RemoveDiscountCode($orderrId: ID!) {
    removeDiscountCode(orderrId: $orderrId) {
      id
    }
  }
`;

// MOB-542 (spec CP-003): the companion product of a lesson product. A lightweight selection:
// never `category` (a companion may have no exported category), `deposit` or `pointMeetings`.
export const getCompanionProduct = /* GraphQL */ `
  query GetCompanionProduct($id: ID!) {
    getProduct(id: $id) {
      id
      mssId
      title
      productType
      pricingMode
      priceTables {
        numberOfPersons
        priceListType
        isLowSeason
        dphPercent
        price075
        price05
        price1
        price1_5
        price2
        price2_5
        price3
        price3_5
        price4
        price4_5
        price5
        price5_5
        price6
        price6_5
        price7
        price7_5
        price8
        price8_5
        price9
        price9_5
        price10
        price10_5
        price11
        price11_5
        price12
        price12_5
        price13
        price13_5
        price14
        price14_5
        price15
        price15_5
        price16
        price16_5
        price17
        price17_5
        price18
        price18_5
        price19
        price19_5
        price20
        price20_5
        priceEveryOther
      }
    }
  }
`;

export const getProductPacket = /* GraphQL */ `
  query GetProductPacket($id: ID!) {
    getProductPacket(id: $id) {
      id
      mobVersion
      groups
      appInstance
      mssId
      title
      description
      image
      orderNumber
      createdAt
      updatedAt
      products {
        items {
          id
          mobVersion
          groups
          appInstance
          productPacketId
          productId
          orderNumber
          preselected
          required
          createdAt
          updatedAt
          product {
            id
            mobVersion
            groups
            appInstance
            mssId
            title
            intro
            description
            productType
            enabledFrom
            enabledTo
            pricingMode
            images
            sizes
            deposit
            orderNumber
            rentProductType
            rentAvailabilityMode
            schoolFormType
            numberOfPersons
            minNumOfPersons
            maxNumOfPersons
            minLessonLength
            maxLessonLength
            defaultLessonLength
            minLength
            maxLength
            minDailyLength
            maxDailyLength
            defaultDailyLength
            allowGroupCourseFlexibleStart
            groupCourseCustomLessonSelection
            timePreferencesStartTimes
            timePreferencesEnabledMonday
            timePreferencesEnabledTuesday
            timePreferencesEnabledWednesday
            timePreferencesEnabledThursday
            timePreferencesEnabledFriday
            timePreferencesEnabledSaturday
            timePreferencesEnabledSunday
            hidden
            campCourse
            textEmail
            priceTables {
              numberOfPersons
              priceListType
              isLowSeason
              dphPercent
              price05
              price1
              price2
              price3
              price4
              price5
              price6
              price7
              price8
              price9
              price10
              price11
              price12
              price13
              price14
              price15
              price16
              price17
              price18
              price19
              price20
              price20_5
              priceEveryOther
            }
            createdAt
            updatedAt
            __typename
          }
          productPacket {
            id
            mobVersion
            groups
            appInstance
            mssId
            title
            description
            image
            orderNumber
            createdAt
            updatedAt
            __typename
          }
          __typename
        }
        nextToken
        __typename
      }
      __typename
    }
  }
`;
